import React, { useState } from 'react';
import axios from '../../utils/axios';
import DishManager from './DishManager';
import { ConfirmDialog } from '../common/ConfirmDialog';
import { showToast } from '../common/ToastNotification';
import { useTranslation } from 'react-i18next';
import { GripVertical, ChevronUp, ChevronDown, Trash2 } from 'lucide-react';

const DRAG_TYPE = { SECTION: 'section', CATEGORY: 'category', DISH: 'dish' };

const CategoryManager = ({ menu, menuId, allergens, onUpdate, onSilentUpdate, onLocalDelete, selectedLanguage }) => {
  const { t } = useTranslation();
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [showAddSection, setShowAddSection] = useState(false);
  const [newCategory, setNewCategory] = useState({ 
    name: '', 
    description: '',
    parentId: null,
    sectionId: ''
  });
  const [newSection, setNewSection] = useState({
    name: '',
    description: ''
  });
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingSection, setEditingSection] = useState(null);
  const [expandedSection, setExpandedSection] = useState(null);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  // Multi-select state
  const [selectedCategoryIds, setSelectedCategoryIds] = useState(new Set());
  const [selectedSectionIds, setSelectedSectionIds] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    type: null,
    id: null,
    ids: null,        // for bulk
    hardDelete: false,
    title: '',
    message: '',
    sectionName: null
  });


  const handleAddCategory = async (e) => {
    e.preventDefault();
    if (!newCategory.name.trim()) {
      showToast.error(t('category_manager.messages.category_name_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const categoryData = {
        ...newCategory,
        sectionId: newCategory.sectionId ? parseInt(newCategory.sectionId) : null
      };
      
      const response = await axios.post(`/menu/management/${menuId}/categories`, categoryData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setNewCategory({ name: '', description: '', parentId: null, sectionId: '' });
        setShowAddCategory(false);
        onUpdate();
        showToast.success(t('category_manager.messages.category_added'));
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_add_category'));
    }
  };

  const handleAddSection = async (e) => {
    e.preventDefault();
    if (!newSection.name.trim()) {
      showToast.error(t('category_manager.messages.section_name_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`/menu/management/${menuId}/sections`, newSection, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setNewSection({ name: '', description: '' });
        setShowAddSection(false);
        onUpdate();
        showToast.success(t('category_manager.messages.section_added'));
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_add_section'));
    }
  };

  const handleUpdateSection = async (e) => {
    e.preventDefault();
    if (!editingSection.name.trim()) {
      showToast.error(t('category_manager.messages.section_name_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/menu/management/sections/${editingSection.id}`, {
        name: editingSection.name,
        description: editingSection.description
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setEditingSection(null);
        showToast.success(t('category_manager.messages.section_updated'));
        onUpdate();
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_update_section'));
    }
  };

  const handleDeleteSection = async (sectionId, hardDelete = false) => {
    const section = menu.MenuSections?.find(s => s.id === sectionId);
    if (!section) return;
    
    const categoryCount = section.Categories?.length || 0;
    const itemCount = section.Categories?.reduce((sum, cat) => sum + (cat.MenuItems?.length || 0), 0) || 0;
    
    const consequence = hardDelete 
      ? t('category_manager.delete_dialog.permanent_consequence_section', { 
          categories: categoryCount, 
          items: itemCount 
        })
      : t('category_manager.delete_dialog.soft_consequence_section', { 
          categories: categoryCount, 
          items: itemCount 
        });
    
    const confirmMessage = t('category_manager.delete_dialog.section_confirm_message', {
      name: section.name,
      consequence: consequence
    });
    
    setDeleteDialog({
      isOpen: true,
      type: 'section',
      id: sectionId,
      hardDelete,
      title: hardDelete 
        ? t('category_manager.delete_dialog.section_confirm_title_hard')
        : t('category_manager.delete_dialog.section_confirm_title_soft'),
      message: confirmMessage,
      sectionName: section.name
    });
  };

  const executeDeleteSection = async () => {
    const id = deleteDialog.id;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/menu/management/sections/${id}?hardDelete=${deleteDialog.hardDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const actionWord = deleteDialog.hardDelete 
          ? t('category_manager.delete_dialog.permanent_delete_action').toLowerCase()
          : t('category_manager.delete_dialog.soft_delete_action');
        showToast.success(t('category_manager.messages.section_deleted', { 
          name: deleteDialog.sectionName, 
          action: actionWord 
        }));
        // Optimistic local update — no full page re-fetch
        if (onLocalDelete) onLocalDelete('section', id);
        else onUpdate();
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_delete_section'));
    }
  };

  const handleUpdateCategory = async (e) => {
    e.preventDefault();
    if (!editingCategory.name.trim()) {
      showToast.error(t('category_manager.messages.category_name_required'));
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(`/menu/categories/${editingCategory.id}`, {
        name: editingCategory.name,
        description: editingCategory.description,
        sectionId: editingCategory.sectionId
      }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.data.success) {
        setEditingCategory(null);
        onUpdate();
        showToast.success(t('category_manager.messages.category_updated'));
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_update_category'));
    }
  };

  const handleDeleteCategory = async (categoryId, hardDelete = false) => {
    let category = null;
    let sectionName = '';
    
    for (const section of menu.MenuSections || []) {
      const foundCat = section.Categories?.find(c => c.id === categoryId);
      if (foundCat) {
        category = foundCat;
        sectionName = section.name;
        break;
      }
    }
    
    if (!category) {
      category = (menu.categories || []).find(c => c.id === categoryId);
    }
    
    if (!category) return;
    
    const itemCount = category.MenuItems?.length || 0;
    
    const consequence = hardDelete 
      ? t('category_manager.delete_dialog.permanent_consequence_category', { 
          items: itemCount 
        })
      : t('category_manager.delete_dialog.soft_consequence_category', { 
          items: itemCount 
        });
    
    const locationInfo = sectionName 
      ? t('category_manager.delete_dialog.location_info', { section: sectionName })
      : '';
    
    const confirmMessage = t('category_manager.delete_dialog.category_confirm_message', {
      name: category.name,
      location: locationInfo,
      consequence: consequence
    });
    
    setDeleteDialog({
      isOpen: true,
      type: 'category',
      id: categoryId,
      hardDelete,
      title: hardDelete 
        ? t('category_manager.delete_dialog.category_confirm_title_hard')
        : t('category_manager.delete_dialog.category_confirm_title_soft'),
      message: confirmMessage,
      sectionName: category.name
    });
  };

  const executeDeleteCategory = async () => {
    const id = deleteDialog.id;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`/menu/categories/${id}?hardDelete=${deleteDialog.hardDelete}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        const actionWord = deleteDialog.hardDelete 
          ? t('category_manager.delete_dialog.permanent_delete_action').toLowerCase()
          : t('category_manager.delete_dialog.soft_delete_action');
        showToast.success(t('category_manager.messages.category_deleted', { 
          name: deleteDialog.sectionName, 
          action: actionWord 
        }));
        // Optimistic local update — no full page re-fetch
        if (onLocalDelete) onLocalDelete('category', id);
        else onUpdate();
        // Clear selection if this category was selected
        setSelectedCategoryIds(prev => { const n = new Set(prev); n.delete(id); return n; });
      }
    } catch (error) {
      showToast.error(error.response?.data?.error || t('category_manager.messages.failed_delete_category'));
    }
  };

  // --- Multi-select helpers ---
  const toggleCategorySelection = (categoryId) => {
    setSelectedCategoryIds(prev => {
      const next = new Set(prev);
      if (next.has(categoryId)) next.delete(categoryId);
      else next.add(categoryId);
      return next;
    });
  };

  const toggleSectionCategoriesSelection = (section) => {
    const catIds = (section.Categories || []).map(c => c.id);
    setSelectedCategoryIds(prev => {
      const allSelected = catIds.every(id => prev.has(id));
      const next = new Set(prev);
      if (allSelected) catIds.forEach(id => next.delete(id));
      else catIds.forEach(id => next.add(id));
      return next;
    });
  };

  const clearSelection = () => {
    setSelectedCategoryIds(new Set());
  };

  const allCategoryIds = (menu.MenuSections || []).flatMap(s => (s.Categories || []).map(c => c.id));

  // --- Bulk delete categories ---
  const handleBulkDeleteCategories = () => {
    if (selectedCategoryIds.size === 0) return;
    const count = selectedCategoryIds.size;
    setDeleteDialog({
      isOpen: true,
      type: 'bulk-category',
      id: null,
      ids: Array.from(selectedCategoryIds),
      hardDelete: true,
      title: t('category_manager.delete_dialog.bulk_confirm_title', { count }),
      message: t('category_manager.delete_dialog.bulk_confirm_message', { count }),
      sectionName: null
    });
  };

  const executeBulkDeleteCategories = async (ids) => {
    setBulkDeleting(true);
    const token = localStorage.getItem('token');
    const results = await Promise.allSettled(
      ids.map(id =>
        axios.delete(`/menu/categories/${id}?hardDelete=true`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      )
    );
    const succeeded = ids.filter((id, i) => results[i].status === 'fulfilled' && results[i].value?.data?.success);
    const failed = ids.length - succeeded.length;
    if (succeeded.length > 0) {
      if (onLocalDelete) onLocalDelete('category', succeeded);
      else onUpdate();
      setSelectedCategoryIds(new Set());
      showToast.success(t('category_manager.messages.bulk_category_deleted', { count: succeeded.length }));
    }
    if (failed > 0) {
      showToast.error(t('category_manager.messages.failed_bulk_delete_category', { count: failed }));
    }
    setBulkDeleting(false);
  };

  const handleDeleteConfirm = () => {
    const { type, ids } = deleteDialog;
    if (type === 'section') {
      executeDeleteSection();
    } else if (type === 'category') {
      executeDeleteCategory();
    } else if (type === 'bulk-category') {
      executeBulkDeleteCategories(ids);
    }
    setDeleteDialog({ isOpen: false, type: null, id: null, ids: null, hardDelete: false, title: '', message: '', sectionName: null });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ isOpen: false, type: null, id: null, ids: null, hardDelete: false, title: '', message: '', sectionName: null });
    showToast.info(t('category_manager.messages.delete_cancelled'));
  };

  const toggleSection = (sectionId) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const handleDishUpdate = () => {
    // Use silent update (no spinner) for dish changes within an expanded category
    if (onSilentUpdate) onSilentUpdate();
    else onUpdate();
  };

  // --- Drag & Drop: Sections reorder (only from section grip); section is also drop target for categories
  const handleSectionDragStart = (e, sectionId) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: DRAG_TYPE.SECTION, id: sectionId }));
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    const card = e.currentTarget.closest('[data-section-card]');
    if (card) card.classList.add('opacity-50');
  };
  const handleSectionDragEnd = (e) => {
    const card = e.currentTarget.closest('[data-section-card]');
    if (card) card.classList.remove('opacity-50');
    setDragOverId(null);
  };
  const handleSectionDragOver = (e, sectionId) => {
    if (e.dataTransfer.types.includes('application/json') || e.dataTransfer.types.includes('text/plain')) {
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setDragOverId(sectionId);
    }
  };
  const handleSectionDrop = async (e, targetSectionId) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    const raw = e.dataTransfer.getData('application/json') || e.dataTransfer.getData('text/plain');
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (_) {
      return;
    }
    if (payload.type === DRAG_TYPE.CATEGORY) {
      const { id: categoryId } = payload;
      try {
        const token = localStorage.getItem('token');
        await axios.put(`/menu/categories/${categoryId}`, { sectionId: targetSectionId }, { headers: { Authorization: `Bearer ${token}` } });
        showToast.success(t('category_manager.messages.category_updated'));
        onUpdate();
      } catch (err) {
        showToast.error(err.response?.data?.error || t('category_manager.messages.failed_move_category') || 'Impossibile spostare la categoria');
      }
      return;
    }
    if (payload.type !== DRAG_TYPE.SECTION) return;
    const { id: sourceId } = payload;
    if (sourceId === targetSectionId) return;
    const sections = [...(menu.MenuSections || [])];
    const fromIdx = sections.findIndex(s => s.id === sourceId);
    const toIdx = sections.findIndex(s => s.id === targetSectionId);
    if (fromIdx === -1 || toIdx === -1) return;
    const [removed] = sections.splice(fromIdx, 1);
    sections.splice(toIdx, 0, removed);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`/menu/management/${menuId}/sections/reorder`, 
        { sections: sections.map((s, i) => ({ id: s.id, position: i })) },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      showToast.success(t('category_manager.messages.section_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      showToast.error(err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_sections') || 'Impossibile riordinare le sezioni');
    }
  };

  const moveSectionUp = async (index) => {
    if (index <= 0) return;
    const sections = [...(menu.MenuSections || [])];
    [sections[index - 1], sections[index]] = [sections[index], sections[index - 1]];
    try {
      const token = localStorage.getItem('token');
      const payload = { sections: sections.map((s, i) => ({ id: s.id, position: i })) };
      await axios.post(`/menu/management/${menuId}/sections/reorder`, payload, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(t('category_manager.messages.section_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_sections') || 'Impossibile riordinare le sezioni';
      showToast.error(msg);
    }
  };
  const moveSectionDown = async (index) => {
    const sections = [...(menu.MenuSections || [])];
    if (index >= sections.length - 1) return;
    [sections[index], sections[index + 1]] = [sections[index + 1], sections[index]];
    try {
      const token = localStorage.getItem('token');
      const payload = { sections: sections.map((s, i) => ({ id: s.id, position: i })) };
      await axios.post(`/menu/management/${menuId}/sections/reorder`, payload, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(t('category_manager.messages.section_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_sections') || 'Impossibile riordinare le sezioni';
      showToast.error(msg);
    }
  };

  // --- Drag & Drop: Category reorder within section or move to another section (only from category grip)
  const handleCategoryDragStart = (e, categoryId, sectionId) => {
    e.dataTransfer.setData('application/json', JSON.stringify({ type: DRAG_TYPE.CATEGORY, id: categoryId, sectionId }));
    e.dataTransfer.effectAllowed = 'move';
    e.stopPropagation();
    const card = e.currentTarget.closest('[data-category-card]');
    if (card) card.classList.add('opacity-50');
  };
  const handleCategoryDragEnd = (e) => {
    const card = e.currentTarget.closest('[data-category-card]');
    if (card) card.classList.remove('opacity-50');
    setDragOverId(null);
  };
  const handleCategoryDragOver = (e, dropTarget) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverId(dropTarget);
  };
  const handleCategoryDrop = async (e, targetSectionId, targetCategoryIdOrNull) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverId(null);
    const raw = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
    if (!raw) return;
    let payload;
    try {
      payload = JSON.parse(raw);
    } catch (_) {
      return;
    }
    // Moving a dish into this category
    if (payload.type === DRAG_TYPE.DISH && payload.id != null) {
      const targetCatId = targetCategoryIdOrNull || menu.MenuSections?.find(s => s.id === targetSectionId)?.Categories?.[0]?.id;
      if (targetCatId && Number(payload.categoryId) !== Number(targetCatId)) {
        await handleMoveDishToCategory(payload.id, targetCatId);
      }
      return;
    }
    if (payload.type !== DRAG_TYPE.CATEGORY) return;
    const { id: categoryId, sectionId: sourceSectionId } = payload;
    const section = menu.MenuSections?.find(s => s.id === sourceSectionId);
    const category = section?.Categories?.find(c => c.id === categoryId) || menu.MenuSections?.flatMap(s => s.Categories || []).find(c => c.id === categoryId);
    if (!category) return;

    if (targetSectionId !== sourceSectionId) {
      try {
        const token = localStorage.getItem('token');
        await axios.put(`/menu/management/categories/${categoryId}`, { sectionId: targetSectionId }, { headers: { Authorization: `Bearer ${token}` } });
        showToast.success(t('category_manager.messages.category_updated'));
        if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
      } catch (err) {
        showToast.error(err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_move_category') || 'Impossibile spostare la categoria');
      }
      return;
    }

    const categories = [...(section.Categories || [])];
    const fromIdx = categories.findIndex(c => c.id === categoryId);
    let toIdx = targetCategoryIdOrNull ? categories.findIndex(c => c.id === targetCategoryIdOrNull) : categories.length - 1;
    if (fromIdx === -1) return;
    if (targetCategoryIdOrNull && toIdx === -1) toIdx = categories.length - 1;
    const [removed] = categories.splice(fromIdx, 1);
    categories.splice(toIdx, 0, removed);
    try {
      const token = localStorage.getItem('token');
      const categoryPayload = categories.map((c, i) => ({ id: c.id, position: i }));
      await axios.put(`/menu/management/${menuId}/categories/positions`, { categories: categoryPayload }, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(t('category_manager.messages.category_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      showToast.error(err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_categories') || 'Impossibile riordinare le categorie');
    }
  };

  const moveCategoryUp = async (section, categoryIndex) => {
    if (categoryIndex <= 0) return;
    const categories = [...(section.Categories || [])];
    [categories[categoryIndex - 1], categories[categoryIndex]] = [categories[categoryIndex], categories[categoryIndex - 1]];
    try {
      const token = localStorage.getItem('token');
      const payload = { categories: categories.map((c, i) => ({ id: c.id, position: i })) };
      await axios.put(`/menu/management/${menuId}/categories/positions`, payload, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(t('category_manager.messages.category_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_categories') || 'Impossibile riordinare le categorie';
      showToast.error(msg);
    }
  };
  const moveCategoryDown = async (section, categoryIndex) => {
    const categories = [...(section.Categories || [])];
    if (categoryIndex >= categories.length - 1) return;
    [categories[categoryIndex], categories[categoryIndex + 1]] = [categories[categoryIndex + 1], categories[categoryIndex]];
    try {
      const token = localStorage.getItem('token');
      const payload = { categories: categories.map((c, i) => ({ id: c.id, position: i })) };
      await axios.put(`/menu/management/${menuId}/categories/positions`, payload, { headers: { Authorization: `Bearer ${token}` } });
      showToast.success(t('category_manager.messages.category_updated'));
      if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_reorder_categories') || 'Impossibile riordinare le categorie';
      showToast.error(msg);
    }
  };

  // Move dish to another category (called from DishManager or when dropping dish on category)
  const handleMoveDishToCategory = async (dishId, targetCategoryId) => {
    try {
      const token = localStorage.getItem('token');
      const id = Number(dishId);
      const catId = Number(targetCategoryId);
      const res = await axios.put(`/menu/management/items/${id}`, { categoryId: catId }, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data && res.data.success !== false) {
        showToast.success(t('category_manager.messages.dishes_updated'));
        if (typeof onUpdate === 'function') await Promise.resolve(onUpdate());
      } else {
        showToast.error(res.data?.error || t('category_manager.messages.failed_move_dish') || 'Impossibile spostare il piatto');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.response?.data?.message || err.message || t('category_manager.messages.failed_move_dish') || 'Impossibile spostare il piatto';
      showToast.error(msg);
    }
  };

  return (
    <div className="p-4 md:p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{t('category_manager.title')}</h1>
            <p className="text-gray-600 mt-1">{t('category_manager.subtitle')}</p>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowAddSection(true)}
              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="font-medium">{t('category_manager.buttons.add_section')}</span>
            </button>
            
            <button
              onClick={() => setShowAddCategory(true)}
              className="flex-1 md:flex-none inline-flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              <span className="font-medium">{t('category_manager.buttons.add_category')}</span>
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-4 rounded-2xl border border-emerald-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-800 font-medium">{t('category_manager.stats.sections')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{menu.MenuSections?.length || 0}</p>
              </div>
              <div className="p-2 bg-emerald-100 rounded-lg">
                <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-2xl border border-blue-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-800 font-medium">{t('category_manager.stats.categories')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {menu.MenuSections?.reduce((sum, section) => sum + (section.Categories?.length || 0), 0) || 0}
                </p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                </svg>
              </div>
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-4 rounded-2xl border border-purple-100">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-purple-800 font-medium">{t('category_manager.stats.total_dishes')}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">
                  {menu.MenuSections?.reduce((sum, section) => 
                    sum + (section.Categories?.reduce((catSum, cat) => 
                      catSum + (cat.MenuItems?.length || 0), 0) || 0), 0) || 0}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Bulk Selection Action Bar ── */}
      {selectedCategoryIds.size > 0 && (
        <div className="sticky top-0 z-20 mb-6 flex flex-wrap items-center gap-3 px-4 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl shadow-lg animate-pulse-once">
          <input
            type="checkbox"
            checked={selectedCategoryIds.size === allCategoryIds.length && allCategoryIds.length > 0}
            onChange={() => {
              if (selectedCategoryIds.size === allCategoryIds.length) clearSelection();
              else setSelectedCategoryIds(new Set(allCategoryIds));
            }}
            className="w-4 h-4 rounded border-white text-emerald-600 focus:ring-white"
          />
          <span className="font-semibold">
            {t('category_manager.action_bar.selected_count', { count: selectedCategoryIds.size })}
          </span>
          <div className="flex-1" />
          <button
            onClick={clearSelection}
            className="text-sm px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
          >
            {t('category_manager.action_bar.clear') || 'Cancella'}
          </button>
          <button
            onClick={handleBulkDeleteCategories}
            disabled={bulkDeleting}
            className="flex items-center gap-2 text-sm px-4 py-1.5 rounded-lg bg-red-500 hover:bg-red-600 disabled:bg-red-400 disabled:cursor-not-allowed font-semibold transition-colors shadow-sm"
          >
            {bulkDeleting ? (
              <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
            ) : (
              <Trash2 className="w-4 h-4" />
            )}
            {t('category_manager.action_bar.delete_selected', { count: selectedCategoryIds.size })}
          </button>
        </div>
      )}

      {/* Add Section Form */}
      {showAddSection && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">{t('category_manager.forms.create_section_title')}</h3>
            <button
              onClick={() => {
                setShowAddSection(false);
                setNewSection({ name: '', description: '' });
              }}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleAddSection} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.section_name')} <span className="text-red-500">{t('category_manager.forms.required')}</span>
              </label>
              <input
                type="text"
                value={newSection.name}
                onChange={(e) => setNewSection({...newSection, name: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder={t('category_manager.forms.section_placeholder')}
                required
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.description')} <span className="text-gray-400">{t('category_manager.forms.optional')}</span>
              </label>
              <textarea
                value={newSection.description}
                onChange={(e) => setNewSection({...newSection, description: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                rows="3"
                placeholder={t('category_manager.forms.section_description_placeholder')}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddSection(false);
                  setNewSection({ name: '', description: '' });
                }}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                {t('category_manager.buttons.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
              >
                {t('category_manager.buttons.create_section')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Add Category Form */}
      {showAddCategory && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">{t('category_manager.forms.create_category_title')}</h3>
            <button
              onClick={() => {
                setShowAddCategory(false);
                setNewCategory({ name: '', description: '', parentId: null, sectionId: '' });
              }}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleAddCategory} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.category_name')} <span className="text-red-500">{t('category_manager.forms.required')}</span>
              </label>
              <input
                type="text"
                value={newCategory.name}
                onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder={t('category_manager.forms.category_placeholder')}
                required
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.section')} <span className="text-red-500">{t('category_manager.forms.required')}</span>
              </label>
              <select
                value={newCategory.sectionId}
                onChange={(e) => setNewCategory({...newCategory, sectionId: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none"
                required
              >
                <option value="">{t('category_manager.forms.select_section')}</option>
                {menu.MenuSections && menu.MenuSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.name}
                  </option>
                ))}
              </select>
              {(!menu.MenuSections || menu.MenuSections.length === 0) && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-800 flex items-center gap-2">
                    <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {t('category_manager.messages.create_section_first')}
                  </p>
                </div>
              )}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.description')} <span className="text-gray-400">{t('category_manager.forms.optional')}</span>
              </label>
              <textarea
                value={newCategory.description}
                onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                rows="3"
                placeholder={t('category_manager.forms.category_description_placeholder')}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => {
                  setShowAddCategory(false);
                  setNewCategory({ name: '', description: '', parentId: null, sectionId: '' });
                }}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                {t('category_manager.buttons.cancel')}
              </button>
              <button
                type="submit"
                disabled={!menu.MenuSections || menu.MenuSections.length === 0}
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t('category_manager.buttons.create_category')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Section Form */}
      {editingSection && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-emerald-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">{t('category_manager.forms.edit_section_title')}</h3>
            <button
              onClick={() => setEditingSection(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleUpdateSection} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.section_name')} <span className="text-red-500">{t('category_manager.forms.required')}</span>
              </label>
              <input
                type="text"
                value={editingSection.name}
                onChange={(e) => setEditingSection({...editingSection, name: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all"
                placeholder={t('category_manager.forms.section_placeholder')}
                required
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.description')} <span className="text-gray-400">{t('category_manager.forms.optional')}</span>
              </label>
              <textarea
                value={editingSection.description || ''}
                onChange={(e) => setEditingSection({...editingSection, description: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition-all resize-none"
                rows="3"
                placeholder={t('category_manager.forms.section_description_placeholder')}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditingSection(null)}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                {t('category_manager.buttons.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
              >
                {t('category_manager.buttons.update_section')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Edit Category Form */}
      {editingCategory && (
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-blue-100">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-bold text-gray-900">{t('category_manager.forms.edit_category_title')}</h3>
            <button
              onClick={() => setEditingCategory(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <form onSubmit={handleUpdateCategory} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.category_name')} <span className="text-red-500">{t('category_manager.forms.required')}</span>
              </label>
              <input
                type="text"
                value={editingCategory.name}
                onChange={(e) => setEditingCategory({...editingCategory, name: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder={t('category_manager.forms.category_placeholder')}
                required
                autoFocus
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-900 mb-2">
                {t('category_manager.forms.description')} <span className="text-gray-400">{t('category_manager.forms.optional')}</span>
              </label>
              <textarea
                value={editingCategory.description || ''}
                onChange={(e) => setEditingCategory({...editingCategory, description: e.target.value})}
                className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all resize-none"
                rows="3"
                placeholder={t('category_manager.forms.category_description_placeholder')}
              />
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={() => setEditingCategory(null)}
                className="px-6 py-3 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium transition-colors"
              >
                {t('category_manager.buttons.cancel')}
              </button>
              <button
                type="submit"
                className="px-6 py-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all shadow-sm hover:shadow-md"
              >
                {t('category_manager.buttons.update_category')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Sections List */}
      <div className="space-y-6">
        {menu.MenuSections && menu.MenuSections.length > 0 ? (
          menu.MenuSections.map((section, sectionIndex) => (
            <div
              key={section.id}
              data-section-card
              className={`bg-white rounded-2xl shadow-sm border overflow-hidden transition-colors ${dragOverId === section.id ? 'border-emerald-400 ring-2 ring-emerald-200' : 'border-gray-100'}`}
              onDragOver={(e) => handleSectionDragOver(e, section.id)}
              onDragLeave={() => setDragOverId(null)}
              onDrop={(e) => handleSectionDrop(e, section.id)}
            >
              {/* Section Header */}
              <div 
                className="p-5 md:p-6 cursor-pointer hover:bg-gray-50 transition-colors"
                onClick={() => toggleSection(section.id)}
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1 min-w-0 flex items-start gap-2">
                    <div className="flex items-center gap-0.5 flex-shrink-0">
                      <span
                        role="button"
                        tabIndex={0}
                        draggable
                        onDragStart={(e) => { e.stopPropagation(); handleSectionDragStart(e, section.id); }}
                        onDragEnd={handleSectionDragEnd}
                        className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600 mt-0.5 touch-none p-0.5"
                        title={t('category_manager.buttons.drag_to_reorder') || 'Trascina per riordinare'}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <GripVertical className="w-5 h-5" />
                      </span>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveSectionUp(sectionIndex); }}
                        disabled={sectionIndex === 0}
                        className="p-1 text-gray-400 hover:text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={t('category_manager.buttons.move_up') || 'Sposta su'}
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); moveSectionDown(sectionIndex); }}
                        disabled={sectionIndex === menu.MenuSections.length - 1}
                        className="p-1 text-gray-400 hover:text-emerald-600 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={t('category_manager.buttons.move_down') || 'Sposta giù'}
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-lg md:text-xl font-bold text-gray-900 truncate">{section.name}</h3>
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full">
                          {section.Categories?.length || 0} {t('category_manager.empty_states.categories').toLowerCase()}
                        </span>
                      </div>
                      
                      {section.description && (
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">{section.description}</p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500">
                        <div className="flex items-center gap-1">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <span>{t('category_manager.empty_states.total_dishes_count', { count: section.Categories?.reduce((sum, cat) => sum + (cat.MenuItems?.length || 0), 0) || 0 })}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
                    {/* Mobile Actions Row - Always visible */}
                    <div className="flex items-center gap-1 sm:hidden">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSection(section);
                        }}
                        className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title={t('category_manager.buttons.edit_section')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section.id, true);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('category_manager.buttons.delete_section')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    
                    {/* Desktop Actions - Always visible on desktop */}
                    <div className="hidden sm:flex items-center gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingSection(section);
                        }}
                        className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                        title={t('category_manager.buttons.edit_section')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteSection(section.id, true);
                        }}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title={t('category_manager.buttons.delete_section')}
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </div>
                    
                    <button className="p-2 text-gray-400 hover:text-gray-600 flex-shrink-0">
                      <svg 
                        className={`w-5 h-5 transform transition-transform ${expandedSection === section.id ? 'rotate-180' : ''}`}
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Section Categories - Collapsible */}
              {expandedSection === section.id && (
                <div className="px-5 md:px-6 pb-6">
                  {section.Categories && section.Categories.length > 0 ? (
                    <div className="space-y-6">
                      <div className="flex items-center justify-between py-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={(section.Categories || []).length > 0 && (section.Categories || []).every(c => selectedCategoryIds.has(c.id))}
                            onChange={(e) => { e.stopPropagation(); toggleSectionCategoriesSelection(section); }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                            title={t('category_manager.checkboxes.select_all_categories') || 'Seleziona tutte le categorie in questa sezione'}
                          />
                          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">{t('category_manager.empty_states.categories')}</h4>
                        </div>
                        <button
                          onClick={() => {
                            setNewCategory({...newCategory, sectionId: section.id});
                            setShowAddCategory(true);
                          }}
                          className="inline-flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-sm rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                          </svg>
                          {t('category_manager.buttons.add_category')}
                        </button>
                      </div>
                      
                      <div className="space-y-4">
                        {section.Categories.map((category, categoryIndex) => (
                          <div
                            key={category.id}
                            data-category-card
                            className={`bg-gradient-to-br from-gray-50 to-white border rounded-xl overflow-hidden transition-all ${
                              selectedCategoryIds.has(category.id)
                                ? 'border-indigo-400 ring-2 ring-indigo-200 bg-indigo-50'
                                : dragOverId === `cat-${category.id}`
                                ? 'border-blue-400 ring-2 ring-blue-200'
                                : 'border-gray-200 hover:border-gray-300'
                            }`}
                            onDragOver={(e) => {
                              if (e.dataTransfer.types.includes('text/plain') || e.dataTransfer.types.includes('application/json')) {
                                e.preventDefault();
                                e.stopPropagation();
                                e.dataTransfer.dropEffect = 'move';
                                setDragOverId(`cat-${category.id}`);
                              }
                            }}
                            onDragLeave={() => setDragOverId(null)}
                            onDrop={(e) => handleCategoryDrop(e, section.id, category.id)}
                          >
                            {/* Category Header */}
                            <div 
                              className="p-4 cursor-pointer hover:bg-gray-50"
                              onClick={() => toggleCategory(category.id)}
                            >
                              <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                                <div className="flex-1 min-w-0 flex items-start gap-2">
                                  <div className="flex items-center gap-0.5 flex-shrink-0">
                                    {/* Checkbox for multi-select */}
                                    <input
                                      type="checkbox"
                                      checked={selectedCategoryIds.has(category.id)}
                                      onChange={(e) => { e.stopPropagation(); toggleCategorySelection(category.id); }}
                                      onClick={(e) => e.stopPropagation()}
                                      className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-1 cursor-pointer"
                                    />
                                    <span
                                      role="button"
                                      tabIndex={0}
                                      draggable
                                      onDragStart={(e) => { e.stopPropagation(); handleCategoryDragStart(e, category.id, section.id); }}
                                      onDragEnd={handleCategoryDragEnd}
                                      className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-blue-600 mt-0.5 touch-none p-0.5"
                                      onClick={(e) => e.stopPropagation()}
                                      title={t('category_manager.buttons.drag_to_reorder') || 'Trascina per riordinare'}
                                    >
                                      <GripVertical className="w-4 h-4" />
                                    </span>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); moveCategoryUp(section, categoryIndex); }}
                                      disabled={categoryIndex === 0}
                                      className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                      title={t('category_manager.buttons.move_up') || 'Sposta su'}
                                    >
                                      <ChevronUp className="w-4 h-4" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={(e) => { e.stopPropagation(); moveCategoryDown(section, categoryIndex); }}
                                      disabled={categoryIndex === section.Categories.length - 1}
                                      className="p-1 text-gray-400 hover:text-blue-600 disabled:opacity-30 disabled:cursor-not-allowed"
                                      title={t('category_manager.buttons.move_down') || 'Sposta giù'}
                                    >
                                      <ChevronDown className="w-4 h-4" />
                                    </button>
                                  </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <h5 className="font-bold text-gray-900 truncate">{category.name}</h5>
                                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">
                                      {category.MenuItems?.length || 0} {t('category_manager.stats.dishes')}
                                    </span>
                                  </div>
                                  {category.description && (
                                    <p className="text-gray-600 text-sm mt-1 line-clamp-2">{category.description}</p>
                                  )}
                                </div>
                                </div>
                                
                                <div className="flex items-center justify-between sm:justify-end gap-2 flex-shrink-0">
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setEditingCategory(category);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                      title={t('category_manager.buttons.edit_category')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                      </svg>
                                    </button>
                                    
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteCategory(category.id, true);
                                      }}
                                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                      title={t('category_manager.buttons.delete_category')}
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                      </svg>
                                    </button>
                                  </div>
                                  
                                  <button className="p-1.5 text-gray-400 hover:text-gray-600">
                                    <svg 
                                      className={`w-4 h-4 transform transition-transform ${expandedCategory === category.id ? 'rotate-180' : ''}`}
                                      fill="none" 
                                      stroke="currentColor" 
                                      viewBox="0 0 24 24"
                                    >
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                  </button>
                                </div>
                              </div>
                            </div>
                            
                            {/* Dish Manager - Collapsible. Wrapper is drop zone for moving dishes into this category. */}
                            {expandedCategory === category.id && (
                              <div
                                className="border-t border-gray-100 min-h-[80px]"
                                onDragOver={(e) => {
                                  const dt = e.dataTransfer;
                                  if (dt.types.includes('text/plain') || dt.types.includes('application/json')) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    dt.dropEffect = 'move';
                                  }
                                }}
                                onDrop={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const raw = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('application/json');
                                  if (!raw) return;
                                  try {
                                    const payload = JSON.parse(raw);
                                    if (payload.type === DRAG_TYPE.DISH && payload.id != null && Number(payload.categoryId) !== Number(category.id)) {
                                      handleMoveDishToCategory(payload.id, category.id);
                                    }
                                  } catch (_) {}
                                }}
                              >
                                <DishManager
                                  menuId={menuId}
                                  categoryId={category.id}
                                  dishes={category.MenuItems || []}
                                  allergens={allergens}
                                  onUpdate={handleDishUpdate}
                                  selectedLanguage={selectedLanguage}
                                  onMoveDishToCategory={handleMoveDishToCategory}
                                />
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="mx-auto w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mb-4">
                        <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                        </svg>
                      </div>
                      <h4 className="text-lg font-medium text-gray-900 mb-2">{t('category_manager.empty_states.no_categories_yet')}</h4>
                      <p className="text-gray-600 mb-4">{t('category_manager.empty_states.add_first_category')}</p>
                      <button
                        onClick={() => {
                          setNewCategory({...newCategory, sectionId: section.id});
                          setShowAddCategory(true);
                        }}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg hover:from-blue-600 hover:to-indigo-700 transition-all"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                        </svg>
                        {t('category_manager.buttons.create_category')}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          ))
        ) : (
          /* Empty State */
          <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl border-2 border-dashed border-gray-300 p-8 md:p-12 text-center">
            <div className="max-w-md mx-auto">
              <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
                <svg className="w-10 h-10 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-3">{t('category_manager.empty_states.no_sections')}</h3>
              <p className="text-gray-600 mb-6">
                {t('category_manager.empty_states.no_sections_description')}
              </p>
              <button
                onClick={() => setShowAddSection(true)}
                className="inline-flex items-center gap-3 px-6 py-3.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl font-medium hover:from-emerald-600 hover:to-teal-700 transition-all shadow-sm hover:shadow-md"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                {t('category_manager.empty_states.create_first_section')}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={deleteDialog.isOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        title={deleteDialog.title}
        message={deleteDialog.message}
        confirmText={deleteDialog.hardDelete ? t('category_manager.delete_dialog.confirm_permanent') : t('category_manager.delete_dialog.confirm_delete')}
        confirmColor="red"
        cancelText={t('category_manager.delete_dialog.cancel')}
        cancelColor="gray"
      />
    </div>
  );
};

export default CategoryManager;